import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, ListMusic, Volume2, VolumeX, ChevronDown, ExternalLink, Youtube, Music2, X } from 'lucide-react';
import { api, mediaUrl } from '@/lib/api';
import { audioBus } from '@/lib/audioBus';
import { jukeboxBus } from '@/lib/jukeboxBus';

type Kind = 'track' | 'spotify' | 'youtube';

interface JItem {
  id: string;
  kind: Kind;
  track_id?: string | null;
  title: string;
  artist: string;
  cover?: string | null;
  audio_url?: string | null;
  external_url?: string | null;
  available: boolean;
}

interface JCfg {
  enabled: boolean;
  shuffle_default: boolean;
  loop_default: boolean;
  items: JItem[];
}

const OWNER = 'jukebox';

const spotifyGlyph = (size = 12) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.6 14.4a.6.6 0 0 1-.9.2c-2.4-1.5-5.5-1.8-9.1-1a.6.6 0 1 1-.3-1.2c3.9-.9 7.3-.6 10 1.1.3.2.4.6.3.9zm1.2-2.7a.8.8 0 0 1-1.1.3c-2.8-1.7-7-2.2-10.3-1.2a.8.8 0 1 1-.5-1.5c3.8-1.2 8.4-.6 11.6 1.4.4.2.5.7.3 1zm.1-2.8c-3.3-2-8.9-2.2-12.1-1.2a1 1 0 1 1-.6-1.9c3.7-1.1 9.9-.9 13.7 1.4a1 1 0 0 1-1 1.7z" />
  </svg>
);

function youtubeId(url?: string | null): string | null {
  if (!url) return null;
  const m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{6,20})/.exec(url);
  return m ? m[1] : null;
}

function spotifyEmbed(url?: string | null): string | null {
  if (!url) return null;
  const m = /open\.spotify\.com\/(track|album|playlist|artist|episode)\/([A-Za-z0-9]{10,30})/.exec(url);
  return m ? `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator` : null;
}

export default function Jukebox() {
  const [cfg, setCfg] = useState<JCfg | null>(null);
  const [queued, setQueued] = useState<JItem[]>([]);
  const [idx, setIdx] = useState<number>(-1);
  const [playing, setPlaying] = useState(false);
  const [open, setOpen] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [loop, setLoop] = useState(true);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [embed, setEmbed] = useState<JItem | null>(null);
  const [visited, setVisited] = useState<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Load config
  useEffect(() => {
    api.get<JCfg>('/public/jukebox').then((r) => {
      setCfg(r.data);
      setShuffle(!!r.data.shuffle_default);
      setLoop(!!r.data.loop_default);
    }).catch(() => setCfg({ enabled: false, shuffle_default: false, loop_default: true, items: [] }));
  }, []);

  // Make sure other audio players stop us
  useEffect(() => {
    const off = audioBus.on((o) => {
      if (o !== OWNER && audioRef.current) {
        audioRef.current.pause();
        setPlaying(false);
      }
    });
    return off;
  }, []);

  // Close popover on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const items = useMemo(() => [...(cfg?.items || []), ...queued], [cfg, queued]);
  const current = idx >= 0 && idx < items.length ? items[idx] : null;

  // Publish live state so external triggers (Hero "Listen Now") can mirror it
  useEffect(() => {
    const title = current ? `${current.title}${current.artist ? ' — ' + current.artist : ''}` : null;
    jukeboxBus.setState({
      ready: cfg !== null,
      enabled: !!cfg?.enabled,
      hasItems: !!cfg?.enabled && items.length > 0,
      playing: playing || !!embed,
      title,
    });
  }, [cfg, items.length, playing, current, embed]);

  const pickNext = useCallback((dir: 1 | -1) => {
    if (items.length === 0) return -1;
    if (shuffle && dir === 1) {
      // pick a random not-recently-visited item
      const left = items.map((_, i) => i).filter((i) => i !== idx && !visited.has(items[i].id));
      if (left.length === 0) {
        if (!loop) return -1;
        setVisited(new Set());
        const pool = items.map((_, i) => i).filter((i) => i !== idx);
        return pool[Math.floor(Math.random() * pool.length)] ?? idx;
      }
      return left[Math.floor(Math.random() * left.length)];
    }
    let n = idx + dir;
    if (n >= items.length) n = loop ? 0 : -1;
    if (n < 0) n = loop ? items.length - 1 : 0;
    return n;
  }, [items, idx, shuffle, loop, visited]);

  const playAt = useCallback((n: number) => {
    if (n < 0 || n >= items.length) {
      setIdx(-1);
      setPlaying(false);
      return;
    }
    const it = items[n];
    setIdx(n);
    setVisited((v) => { const next = new Set(v); next.add(it.id); return next; });
    if (it.kind !== 'track') {
      // open the embed modal, but don't try to play audio inline
      audioRef.current?.pause();
      setPlaying(false);
      setEmbed(it);
      return;
    }
    audioBus.claim(OWNER);
    const audio = audioRef.current;
    if (!audio || !it.audio_url) return;
    audio.src = mediaUrl(it.audio_url);
    audio.currentTime = 0;
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [items]);

  const togglePlay = () => {
    if (idx < 0 && items.length > 0) {
      playAt(shuffle ? Math.floor(Math.random() * items.length) : 0);
      return;
    }
    if (current?.kind !== 'track') {
      // external — just reopen the embed
      if (current) setEmbed(current);
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audioBus.claim(OWNER);
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  const onEnded = () => {
    const next = pickNext(1);
    if (next === -1) {
      setPlaying(false);
      return;
    }
    playAt(next);
  };

  // External command handler (e.g. Hero "Listen Now" button)
  const latestRef = useRef({ playAt, togglePlay, items, idx, embed });
  latestRef.current = { playAt, togglePlay, items, idx, embed };
  useEffect(() => {
    const findFirst = (list: JItem[]) => {
      const t = list.findIndex((it) => it.kind === 'track' && it.available !== false && !!it.audio_url);
      if (t >= 0) return t;
      return list.length > 0 ? 0 : -1;
    };
    const off = jukeboxBus.onCmd((cmd) => {
      const { playAt, togglePlay, items: currentItems, idx, embed } = latestRef.current;
      if (cmd.type === 'playFirst') {
        if (currentItems.length === 0) return;
        setOpen(true);
        const n = idx >= 0 ? idx : findFirst(currentItems);
        if (n >= 0) playAt(n);
      } else if (cmd.type === 'toggle') {
        if (currentItems.length === 0) return;
        // If an external embed modal is currently open, treat toggle as pause → close it
        if (embed) { setEmbed(null); return; }
        setOpen(true);
        if (idx < 0) {
          const n = findFirst(currentItems);
          if (n >= 0) playAt(n);
        } else {
          togglePlay();
        }
      } else if (cmd.type === 'pause') {
        if (embed) setEmbed(null);
        const a = audioRef.current;
        if (a && !a.paused) { a.pause(); setPlaying(false); }
      } else if (cmd.type === 'enqueueTrack') {
        const alreadyQueued = currentItems.some((it) => it.track_id === cmd.track.id);
        if (!alreadyQueued) {
          setQueued((queue) => [
            ...queue,
            {
              id: `featured-${cmd.track.id}`,
              track_id: cmd.track.id,
              kind: 'track',
              title: cmd.track.title,
              artist: cmd.track.artist,
              cover: cmd.track.coverUrl,
              audio_url: cmd.track.audioUrl,
              external_url: cmd.track.externalUrl,
              available: true,
            },
          ]);
        }
        setOpen(true);
      }
    });
    return off;
  }, []);

  const prettyTime = (s: number) => {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const mm = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    const audio = audioRef.current;
    if (audio && Number.isFinite(duration) && duration > 0) {
      audio.currentTime = (v / 100) * duration;
      setProgress(v);
    }
  };

  const kindTag = (k: Kind) => k === 'track' ? '' : k === 'spotify' ? 'Spotify' : 'YouTube';
  const KindIcon = ({ k }: { k: Kind }) => k === 'youtube' ? <Youtube size={12} /> : k === 'spotify' ? spotifyGlyph(12) : <Music2 size={12} />;

  if (!cfg || !cfg.enabled || items.length === 0) return null;

  const label = current ? `${current.title}${current.artist ? ' — ' + current.artist : ''}` : 'The Ekuba Jukebox';

  return (
    <>
      <div className="ek-jukebox" data-testid="jukebox-widget">
        <button
          type="button"
          className={`ek-jk-trigger ${playing ? 'is-playing' : ''}`}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label="Open jukebox"
          data-testid="jukebox-trigger"
        >
          <span className="ek-jk-disc" aria-hidden="true">
            {current?.cover ? (
              <img src={mediaUrl(current.cover)} alt="" />
            ) : (
              <ListMusic size={14} />
            )}
            <span className={`ek-jk-dot ${playing ? 'is-live' : ''}`} />
          </span>
          <span className="ek-jk-label">
            <span className="ek-jk-eyebrow">JUKEBOX</span>
            <span className="ek-jk-title">{label}</span>
          </span>
          <ChevronDown size={12} className={`ek-jk-chevron ${open ? 'is-open' : ''}`} />
        </button>

        {open && (
          <div ref={popRef} className="ek-jk-popover" role="dialog" aria-label="Jukebox player" data-testid="jukebox-popover">
            <div className="ek-jk-now">
              <div className="ek-jk-cover">
                {current?.cover ? (
                  <img src={mediaUrl(current.cover)} alt="" />
                ) : (
                  <ListMusic size={22} />
                )}
              </div>
              <div className="ek-jk-meta">
                <div className="ek-jk-now-title" title={current?.title}>{current?.title || 'Press play'}</div>
                <div className="ek-jk-now-artist" title={current?.artist}>
                  {current?.artist || 'Pick a track from the queue'}
                  {current && current.kind !== 'track' && (
                    <span className="ek-jk-kind-tag">
                      <KindIcon k={current.kind} /> {kindTag(current.kind)}
                    </span>
                  )}
                </div>
                <div className="ek-jk-progress">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={0.1}
                    value={current?.kind === 'track' ? progress : 0}
                    onChange={seek}
                    aria-label="Seek"
                    disabled={!current || current.kind !== 'track'}
                    data-testid="jukebox-seek"
                  />
                  <div className="ek-jk-times">
                    <span>{current?.kind === 'track' ? prettyTime((progress / 100) * duration) : '—'}</span>
                    <span>{current?.kind === 'track' ? prettyTime(duration) : (current ? kindTag(current.kind) : '—')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="ek-jk-controls">
              <button type="button" className={`ek-jk-btn ${shuffle ? 'is-on' : ''}`} onClick={() => setShuffle((s) => !s)} aria-pressed={shuffle} aria-label="Shuffle" title="Shuffle" data-testid="jukebox-shuffle">
                <Shuffle size={15} />
              </button>
              <button type="button" className="ek-jk-btn" onClick={() => playAt(pickNext(-1))} aria-label="Previous" title="Previous" data-testid="jukebox-prev">
                <SkipBack size={16} />
              </button>
              <button type="button" className="ek-jk-btn is-primary" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} data-testid="jukebox-play">
                {playing ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <button type="button" className="ek-jk-btn" onClick={() => playAt(pickNext(1))} aria-label="Next" title="Next" data-testid="jukebox-next">
                <SkipForward size={16} />
              </button>
              <button type="button" className={`ek-jk-btn ${loop ? 'is-on' : ''}`} onClick={() => setLoop((v) => !v)} aria-pressed={loop} aria-label="Loop" title="Loop playlist" data-testid="jukebox-loop">
                <Repeat size={15} />
              </button>
              <button type="button" className="ek-jk-btn" onClick={toggleMute} aria-pressed={muted} aria-label={muted ? 'Unmute' : 'Mute'} data-testid="jukebox-mute">
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
            </div>

            <div className="ek-jk-divider" />

            <ul className="ek-jk-list" data-testid="jukebox-list">
              {items.map((it, i) => (
                <li key={it.id}>
                  <button
                    type="button"
                    className={`ek-jk-list-row ${i === idx ? 'is-active' : ''}`}
                    onClick={() => playAt(i)}
                    data-testid={`jukebox-list-row-${i}`}
                  >
                    <span className="ek-jk-list-cover">
                      {it.cover ? (
                        <img src={mediaUrl(it.cover)} alt="" />
                      ) : it.kind === 'spotify' ? spotifyGlyph(14) : it.kind === 'youtube' ? <Youtube size={14} /> : <Music2 size={14} />}
                    </span>
                    <span className="ek-jk-list-meta">
                      <span className="ek-jk-list-title">{it.title || 'Untitled'}</span>
                      <span className="ek-jk-list-artist">{it.artist || (it.kind === 'spotify' ? 'Spotify' : it.kind === 'youtube' ? 'YouTube' : '')}</span>
                    </span>
                    {i === idx && playing && current?.kind === 'track' ? (
                      <span className="ek-jk-eq" aria-hidden="true"><span /><span /><span /></span>
                    ) : it.kind !== 'track' ? (
                      <ExternalLink size={12} className="ek-jk-list-ext" />
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Hidden audio element: only used for 'track' items */}
        <audio
          ref={audioRef}
          preload="none"
          onEnded={onEnded}
          onTimeUpdate={(e) => {
            const a = e.currentTarget;
            if (a.duration > 0) setProgress((a.currentTime / a.duration) * 100);
          }}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />
      </div>

      {embed && (
        <div className="ek-jk-embed-backdrop" role="dialog" aria-label={`${embed.title} player`} onClick={() => setEmbed(null)} data-testid="jukebox-embed">
          <div className="ek-jk-embed" onClick={(e) => e.stopPropagation()}>
            <div className="ek-jk-embed-top">
              <div>
                <div className="ek-jk-embed-kicker">{kindTag(embed.kind)}</div>
                <div className="ek-jk-embed-title">{embed.title || embed.artist || 'Now playing'}</div>
              </div>
              <button type="button" className="ek-jk-embed-close" onClick={() => setEmbed(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            {embed.kind === 'spotify' && spotifyEmbed(embed.external_url) && (
              <iframe
                title={embed.title || 'Spotify'}
                src={spotifyEmbed(embed.external_url)!}
                width="100%" height="232" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"
              />
            )}
            {embed.kind === 'youtube' && youtubeId(embed.external_url) && (
              <iframe
                title={embed.title || 'YouTube'}
                src={`https://www.youtube.com/embed/${youtubeId(embed.external_url)}?autoplay=1&rel=0`}
                width="100%" height="320" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen loading="lazy"
              />
            )}
            {embed.external_url && (
              <a href={embed.external_url} target="_blank" rel="noopener noreferrer" className="ek-jk-embed-ext">
                Open on {kindTag(embed.kind)} <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
      )}
    </>
  );
}
